/**
 * MEU PERFIL — quem você é no sistema, e o que o seu papel alcança.
 *
 * ## De onde esta tela veio, e por que ela não é mais o login
 *
 * Todo o conteúdo abaixo morava na TELA DE LOGIN. Ele estava lá por um motivo
 * que fazia sentido na época: a semeadura criava pessoa para cinco dos nove
 * perfis, então não havia como *ser* um planejador de manutenção ou alguém de
 * compras — o documento no login fazia o papel de substituto, e quem quisesse
 * saber o que aquele perfil veria lia a proposta em vez de entrar e olhar.
 *
 * Com conta para os nove, o substituto perdeu a razão de existir, e o dono
 * apontou isso: *"por que proposta de permissão não saiu do login sendo que tem
 * um e-mail para cada persona?"*. Três coisas estavam erradas em manter lá:
 *
 *  1. Ficava **visível sem sessão**. Quem alcançasse o endereço lia o desenho
 *     de permissões do sistema inteiro antes de entrar.
 *  2. O público era **uma pessoa decidindo a matriz**, e essa decisão acontece
 *     dentro do sistema, ao lado de papel, permissão e alçada.
 *  3. Tela de entrada é para **entrar**. O documento era maior que o formulário.
 *
 * ## E por que ela mostra SÓ o seu perfil, sem seletor
 *
 * A decisão é do dono, com estas palavras: *"fica dentro do sistema, no perfil
 * do usuário logado, a descrição completa da persona"*. Não é um catálogo dos
 * nove com um seletor — é a sua ficha. Para ver outro perfil, entra-se com
 * outra conta, que é o que o sistema passou a permitir.
 *
 * A diferença não é de arrumação. Um seletor de perfil aqui diria "escolha uma
 * persona", e persona escolhida num seletor foi exatamente o que esta tela
 * deixou de ser quando a autenticação passou a existir: o papel agora vem da
 * CREDENCIAL, não de uma caixa de seleção.
 *
 * ## O que continua sendo proposta, e a tela diz isso
 *
 * A matriz de permissões **não existe** na documentação — o guia a lista três
 * vezes como pendência, e no Apêndice A a caixa está desmarcada. O que aparece
 * como "o que você pode ler e escrever" foi DERIVADO da necessidade principal e
 * das decisões apoiadas da seção 1.4, e cada linha carrega de onde saiu, para
 * dar para conferir contra o documento e discordar.
 *
 * E o aviso que a tela nao pode errar: desde 21/09 o servidor CONFERE a
 * permissao do papel em cada pedido (`backend/src/autorizacao/portao.ts`).
 * Esta pagina ja disse o contrario ("o papel nao barra nada") depois de isso
 * deixar de ser verdade; `textos.test.ts` impede a frase de voltar.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { type ReactNode } from 'react';

import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { RodapeDeContagem, Tabela, type Coluna } from '@/componentes/Tabela';
import { CAMINHO_DO_LOGIN } from '@/componentes/Casca';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarData } from '@/lib/formato';
import { comCorpo, pedir } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import {
  alcanceDoPerfil,
  notaDeModuloNaoConstruido,
  type AcaoConcedida,
  type AlcanceDoPerfil,
} from './consultas';
import {
  DIMENSOES_DE_ESCOPO,
  type PerfilDecididoPeloProjeto,
  type PerfilDisponivel,
} from './perfis';
import type { AcaoDoSprint } from './sprint';
import { SegundoFator } from './SegundoFator';
import { TrocaDeSenha } from './TrocaDeSenha';
import { VinculoDaConta } from './VinculoDaConta';
import { ROTULO_DE_SUPORTE } from '@/modulos/sessao/conta-de-suporte';

export function PaginaPerfil() {
  const navegar = useNavigate();
  const sessao = useSessao();

  /* CARREGANDO e VAZIO previstos, que é a regra 9 do manual. Carregando é
     esqueleto na forma da página (p.32), e não a frase: o cabeçalho, o título
     dos blocos e os rótulos não dependem da sessão; o que vem da credencial
     vira barra. */
  if (sessao.data === undefined) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Seu acesso"
            titulo="Meu perfil"
            subtitulo="Quem você é no sistema, e o que o seu papel alcança"
            acoes={
              <Link to="/previa" className="botao botao-terciario">
                Ver a prévia do sistema
              </Link>
            }
          />
          <Bloco
            titulo="Identificação"
            apoio="Vem da sua credencial, e não desta tela. A empresa é o recorte de tudo o que você vê no sistema."
          >
            <Carregando
              oQue="Carregando o seu perfil"
              className="grid grid-cols-[repeat(auto-fill,minmax(min(var(--campo-min),100%),1fr))] gap-x-6 gap-y-3"
            >
              {['Nome', 'E-mail', 'Empresa', 'Papel'].map((rotulo, i) => (
                <div key={rotulo} aria-hidden="true" className="flex flex-col gap-1">
                  <span className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</span>
                  <Barra className={`my-1 h-3 ${i % 2 === 0 ? 'w-3/4' : 'w-1/2'}`} />
                </div>
              ))}
            </Carregando>
            <div aria-hidden="true" className="flex max-w-[72ch] flex-col gap-2 border-t border-borda-sutil pt-4">
              <Barra className="my-1 h-3 w-full" />
              <Barra className="my-1 h-3 w-11/12" />
              <Barra className="my-1 h-3 w-4/5" />
            </div>
          </Bloco>
          <div aria-hidden="true">
            <Bloco
              titulo="Senha"
              apoio="Para trocar, diga a senha atual. Errar a senha atual conta nas tentativas de entrada do seu e-mail."
            >
              <div className="flex flex-col gap-4">
                {['Senha atual', 'Senha nova', 'Repita a senha nova'].map((rotulo) => (
                  <span key={rotulo} className="flex flex-col gap-2">
                    <span className="txt-rotulo-campo">{rotulo}</span>
                    <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
                  </span>
                ))}
              </div>
            </Bloco>
          </div>
        </div>
      </div>
    );
  }

  /* A casca já manda para o login quem não tem sessão, então este ramo é a
     corrida entre a expiração e o clique. Ele diz o que fazer, e não só que
     algo faltou. */
  if (sessao.data === null) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <EstadoVazio
            titulo="Sua sessão não está mais aberta"
            descricao="O prazo da sessão terminou, ou ela foi encerrada. Entre outra vez para ver o seu perfil."
            acao={
              <Botao
                nivel="secundario"
                onClick={() => {
                  void navegar({ to: CAMINHO_DO_LOGIN });
                }}
              >
                Voltar à entrada
              </Botao>
            }
          />
        </div>
      </div>
    );
  }

  const eu = sessao.data;
  const alcance = eu.perfil === null ? null : alcanceDoPerfil(eu.perfil);

  /* O perfil decidido pelo projeto (hoje, só o administrador) tem duas partes
     que os oito do guia não têm: as ressalvas, no cartão, e a pergunta em
     aberto, no bloco de escopo. O tipo é que garante isso — ver `perfis.ts`. */
  const perfilDecidido: PerfilDecididoPeloProjeto | null =
    alcance !== null && alcance.perfilDaProposta.perfil.origem === 'decisao-do-projeto'
      ? alcance.perfilDaProposta.perfil
      : null;

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Seu acesso"
          titulo="Meu perfil"
          subtitulo="Quem você é no sistema, e o que o seu papel alcança"
          /* A PREVIA DO SISTEMA (modulo 22) entra por aqui, e nao pelo menu
             lateral: o vocabulario de icones do manual nao tem entrada para
             "previa", e inventar uma seria decisao de quem assina o manual.
             Este e o lugar certo enquanto isso — quem vai apresentar o sistema
             ja passa por aqui para ver o que cada papel enxerga. */
          acoes={
            <Link to="/previa" className="botao botao-terciario">
              Ver a prévia do sistema
            </Link>
          }
        />

        {/* ---------------------------------------------- quem você é, de fato */}
        {/* Este bloco é o único da página que é FATO e não proposta: cada linha
            vem da sua credencial, pelo banco. A empresa está aqui porque ela é
            o recorte de tudo o que o sistema mostra — a mesma consulta, com
            outra credencial, devolve outra frota. */}
        <Bloco
          titulo="Identificação"
          apoio="Vem da sua credencial, e não desta tela. A empresa é o recorte de tudo o que você vê no sistema."
        >
          {/* Dados curtos lado a lado, em grade (R01, p.27-28: a largura do
              campo e a do dado). Numa coluna so, quatro linhas curtas ficavam
              empilhadas num cartao de 1.600 px. */}
          <dl className="grid grid-cols-[repeat(auto-fill,minmax(min(var(--campo-min),100%),1fr))] gap-x-6 gap-y-3">
            <Linha rotulo="Nome" valor={eu.nome} />
            <Linha rotulo="E-mail" valor={eu.email ?? 'Sem e-mail cadastrado'} />
            <Linha rotulo="Empresa" valor={eu.empresa} />
            <Linha
              rotulo={eu.contaDeSuporte?.vinculada === true || eu.papeis.length === 1 ? 'Papel' : 'Papéis'}
              valor={
                /* A conta de suporte (0134): o papel continua o que concede; o nome e Suporte. */
                eu.contaDeSuporte?.vinculada === true
                  ? ROTULO_DE_SUPORTE
                  : eu.papeis.length === 0
                  ? 'Nenhum papel cadastrado'
                  : eu.papeis.map((p) => p.nome).join(' · ')
              }
            />
          </dl>

          {/* DUAS RESSALVAS QUE A PÁGINA NÃO PODE CALAR. A primeira dizia, até
              22/09, que o papel "não barra nada" — verdade quando foi escrita e
              falsa desde 21/09, quando o portão passou a conferir permissão em
              toda rota. Ficou um dia no QAS dizendo a quem entrava o contrário
              do que o servidor faz. O que ela diz agora é o limite que continua
              de pé: o papel vale na empresa inteira. */}
          <ul className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
            <Aviso>
              O servidor confere a permissão do seu papel em cada pedido: digitar o endereço
              de um módulo não abre o que o papel não permite. Um papel concedido só numa
              filial mostra só as máquinas da filial e as que estão numa obra dela; o
              escopo por obra ainda não existe.
            </Aviso>
            <Aviso>
              O segundo fator é escolha sua, no bloco abaixo. Se você esqueceu a senha, peça a quem
              administra o sistema um convite de troca de senha: ele troca a senha e não desliga o
              segundo fator.
            </Aviso>
          </ul>
        </Bloco>

        {/* ------------------------------------------------ trocar a senha (0025) */}
        <Bloco
          titulo="Senha"
          apoio="Para trocar, diga a senha atual. Errar a senha atual conta nas tentativas de entrada do seu e-mail."
        >
          <TrocaDeSenha email={eu.email} />
        </Bloco>

        {/* ---------------------------------- a conta de suporte (0134) */}
        {eu.contaDeSuporte ? (
          <Bloco
            titulo="Administração da plataforma"
            apoio="Você administra a plataforma com este mesmo e-mail. Vinculadas, as duas contas têm uma senha só, e você troca de ambiente pelo botão ao lado de Sair, sem entrar de novo."
          >
            <VinculoDaConta conta={eu.contaDeSuporte} email={eu.email} />
          </Bloco>
        ) : null}

        {/* ------------------------------------------ segundo fator (0085) */}
        {/* O cartão próprio da R06 (p.68): ícone, título e o selo da situação. */}
        <SegundoFator conta={eu.email ?? eu.nome} />

        {/* -------------------------------- aparelhos com biometria (0075, M29) */}
        <Bloco
          titulo="Aparelhos com biometria"
          apoio="Os aparelhos que entram na sua conta com o rosto ou a digital. A biometria se ativa no app do celular; aqui você tira o aparelho que perdeu ou trocou. A senha continua valendo."
        >
          <AparelhosComBiometria />
        </Bloco>

        {/* ------------------------------------------- a descrição da persona */}
        {alcance === null ? (
          <Bloco titulo="A descrição do seu perfil">
            <EstadoVazio
              titulo={
                eu.papeis.length === 0
                  ? 'Você não tem papel cadastrado'
                  : 'O seu papel não está no mapa de perfis'
              }
              descricao={
                eu.papeis.length === 0
                  ? 'Sem papel, você não tem permissão em nenhum módulo: o servidor confere a permissão em cada pedido, e o menu mostra só o que não depende dela. Peça a quem administra o sistema para conceder um papel.'
                  : `O guia de construção descreve nove perfis, e ${eu.papeis.map((p) => p.nome).join(', ')} não é um deles, então não há descrição para mostrar aqui. O que você abre continua vindo das permissões do seu papel. Peça a quem administra o sistema para vincular um dos perfis do guia.`
              }
            />
          </Bloco>
        ) : (
          <Bloco
            titulo="A descrição do seu perfil"
            apoio="Esta descrição saiu do guia de construção: o que cada perfil precisa ver e decidir, e cada linha diz de onde veio. O que você abre de verdade é o que as permissões do seu papel deixam. Ajustar um papel é cadastro na tela de governança."
          >
            <div className="flex flex-col gap-6">
              <CartaoDoPerfil perfil={alcance.perfilDaProposta.perfil} />
              <Detalhamento alcance={alcance} />
            </div>
          </Bloco>
        )}

        {/* ------------------------------------------------- escopo além do papel */}
        <HistoriaNaMesa codigo="NUM-ACS-03">
          <Bloco
            titulo="Escopo além do papel"
            apoio="O guia pede escopo por empresa, filial, contrato e função. A empresa vem da sua credencial e recorta tudo o que você vê. A filial vale quando o papel é concedido só nela. Contrato e função ainda não restringem nada, e aparecem aqui como texto — um seletor que não muda nada faria parecer que o isolamento está quebrado."
          >
            <dl className="flex flex-col gap-3">
              {DIMENSOES_DE_ESCOPO.map((dimensao) => (
                <div key={dimensao.nome} className="flex flex-col gap-1">
                  <dt className="txt-corpo text-tinta">{dimensao.nome}</dt>
                  {/* A situacao, em portugues da obra. A secao do guia de onde a
                      dimensao saiu (`fonte`) fica no codigo, e nao na tela. */}
                  <dd className="txt-apoio max-w-[72ch] text-pretty text-tinta-secundaria">{dimensao.situacao}</dd>
                </div>
              ))}
            </dl>

            {perfilDecidido === null ? null : (
              <div className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
                <h3 className="txt-subtitulo text-tinta">
                  Pergunta em aberto sobre {perfilDecidido.nome}
                </h3>
                <ul className="flex flex-col gap-2">
                  {perfilDecidido.perguntasAbertas.map((pergunta) => (
                    <li key={pergunta} className="txt-apoio max-w-[72ch] text-pretty text-tinta-secundaria">
                      {pergunta}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Bloco>
        </HistoriaNaMesa>
      </div>
    </div>
  );
}

function Detalhamento({ alcance }: { readonly alcance: AlcanceDoPerfil }) {
  return (
    <div className="flex flex-col gap-6">
      <Secao
        titulo="Módulos que este perfil vê"
        apoio="É o que a navegação lateral mostra para quem tem este papel cadastrado."
      >
        <ul className="flex flex-col gap-3">
          {alcance.modulos.map((alcancado) => (
            <Item
              key={alcancado.modulo.id}
              titulo={alcancado.modulo.rotulo}
              marcador={alcancado.construido ? null : <Marcador>Não construído</Marcador>}
              porque={alcancado.porque}
            >
              {alcancado.construido ? null : notaDeModuloNaoConstruido(alcancado.modulo)}
            </Item>
          ))}
        </ul>
      </Secao>

      <Secao titulo="O que pode ler">
        <ListaDeAcoes concedidas={alcance.leitura} vazio="Nenhuma leitura neste perfil." />
      </Secao>

      <Secao titulo="O que pode escrever">
        <ListaDeAcoes
          concedidas={alcance.escrita}
          vazio="Nenhuma escrita neste perfil."
        />
      </Secao>

      <Secao
        titulo="O que não alcança"
        apoio="Decisão de permissão: está na tela e este perfil não recebe. Parte destes controles está desligada — concedê-los não mudaria nada hoje, e o marcador diz quais."
      >
        {alcance.modulosForaDoPerfil.length === 0 && alcance.acoesForaDoPerfil.length === 0 ? (
          <p className="txt-apoio text-tinta-secundaria">
            Este perfil alcança tudo o que o sistema tem hoje.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {alcance.modulosForaDoPerfil.map((modulo) => (
              <Item
                key={modulo.id}
                titulo={modulo.rotulo}
                marcador={<Marcador>Módulo</Marcador>}
                porque="fora deste perfil na matriz"
              />
            ))}
            {alcance.acoesForaDoPerfil.map((acao) => (
              <Item
                key={acao.id}
                titulo={acao.rotulo}
                marcador={
                  <>
                    <Marcador>{acao.tipo === 'ler' ? 'Leitura' : 'Escrita'}</Marcador>
                    {marcadorDaSituacao(acao)}
                  </>
                }
                porque="fora deste perfil na matriz"
              >
                {textoDaSituacao(acao)}
              </Item>
            ))}
          </ul>
        )}
      </Secao>

      <Secao
        titulo="Ainda não construído"
        apoio="Fato sobre o sistema, e não permissão negada. Estes módulos não existem para ninguém."
      >
        {alcance.modulosNaoConstruidos.length === 0 ? (
          <p className="txt-apoio text-tinta-secundaria">
            Todos os módulos fora deste perfil já existem no sistema.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {alcance.modulosNaoConstruidos.map((modulo) => (
              <ItemSimples key={modulo.id} titulo={modulo.rotulo}>
                {notaDeModuloNaoConstruido(modulo)}
              </ItemSimples>
            ))}
          </ul>
        )}
      </Secao>

      <Secao
        titulo="O que falta para este perfil"
        apoio="O que a necessidade do perfil pede e o sistema ainda não tem. Não é permissão: é o que falta construir."
      >
        <ul className="flex flex-col gap-2">
          {alcance.naoTemAcao.map((falta) => (
            <li key={falta} className="txt-apoio text-tinta-secundaria">
              {falta}
            </li>
          ))}
        </ul>
      </Secao>
    </div>
  );
}

function ListaDeAcoes({
  concedidas,
  vazio,
}: {
  readonly concedidas: readonly AcaoConcedida[];
  readonly vazio: string;
}) {
  if (concedidas.length === 0) {
    return <p className="txt-apoio text-tinta-secundaria">{vazio}</p>;
  }

  return (
    <ul className="flex flex-col gap-3">
      {concedidas.map(({ acao, porque }) => (
        <Item
          key={acao.id}
          titulo={acao.rotulo}
          marcador={marcadorDaSituacao(acao)}
          porque={porque}
        >
          {acao.detalhe}
        </Item>
      ))}
    </ul>
  );
}

/** O marcador diz QUAL dos dois nãos é: o botão existe e não responde, ou não há botão. */
function marcadorDaSituacao(acao: AcaoDoSprint) {
  if (acao.situacao === 'desligada') return <Marcador>Desligada</Marcador>;
  if (acao.situacao === 'nao-desenhada') return <Marcador>Não desenhada</Marcador>;
  return null;
}

function textoDaSituacao(acao: AcaoDoSprint): string {
  return acao.situacao === 'funciona'
    ? acao.detalhe
    : `${acao.detalhe} Conceder esta ação não mudaria nada hoje.`;
}

/* ------------------------------------------------- aparelhos com biometria */

/** Um aparelho com a biometria ativa, como `GET /sessao/biometria` o devolve. */
interface AparelhoComBiometria {
  readonly id: string;
  readonly aparelho: string;
  readonly criadaEm: string;
  readonly usadaEm: string | null;
}

const CHAVE_DOS_APARELHOS = ['sessao', 'biometria'] as const;

/**
 * A lista que o celular mostra em Perfil › Aparelhos conectados (M29), na
 * mesa: quem perdeu o telefone tira o aparelho daqui. Tirar pede confirmação
 * (p.30) e o retorno é o toast da R04.
 */
function AparelhosComBiometria() {
  const cliente = useQueryClient();
  const aparelhos = useQuery({
    queryKey: CHAVE_DOS_APARELHOS,
    queryFn: async () => (await pedir<{ aparelhos: readonly AparelhoComBiometria[] }>('/sessao/biometria')).aparelhos,
  });
  const tirar = useMutation({
    mutationFn: (a: AparelhoComBiometria) => comCorpo<unknown>('DELETE', `/sessao/biometria/${encodeURIComponent(a.id)}`),
    meta: {
      retorno: retornoDe<unknown, AparelhoComBiometria>((_r, a) => ({
        titulo: 'Aparelho tirado da biometria',
        alvo: { nome: a.aparelho },
        fatos: [`ativado em ${formatarData(new Date(a.criadaEm))}`],
        efeito: 'Ele não entra mais com a biometria; a senha continua valendo',
      })),
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_DOS_APARELHOS });
    },
  });

  const colunas: readonly Coluna<AparelhoComBiometria>[] = [
    { chave: 'aparelho', rotulo: 'Aparelho', celula: (a) => a.aparelho },
    { chave: 'ativado', rotulo: 'Ativado em', celula: (a) => formatarData(new Date(a.criadaEm)) },
    { chave: 'usado', rotulo: 'Último uso', celula: (a) => (a.usadaEm ? formatarData(new Date(a.usadaEm)) : 'Ainda não usado') },
    {
      chave: 'acao',
      rotulo: 'Ação',
      alinhamento: 'numero',
      celula: (a) => (
        <BotaoDestrutivo
          rotulo="Tirar aparelho"
          pergunta={`Tirar ${a.aparelho} da biometria?`}
          consequencia="Ele deixa de entrar com o rosto ou a digital. A senha continua valendo, e a biometria pode ser ativada de novo pelo perfil do app."
          rotuloConfirmar="Tirar aparelho"
          rotuloCancelar="Manter aparelho"
          {...(tirar.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
          aoConfirmar={() => {
            tirar.mutate(a);
          }}
        />
      ),
    },
  ];
  const lista = aparelhos.data ?? [];

  return (
    <Tabela
      rotulo="Aparelhos com biometria"
      colunas={colunas}
      linhas={lista}
      chaveDaLinha={(a) => a.id}
      carregando={aparelhos.isPending}
      erro={
        aparelhos.isError ? (
          <ErroDaLista
            oQue="Os aparelhos com biometria"
            erro={aparelhos.error}
            aoTentarDeNovo={() => {
              void aparelhos.refetch();
            }}
          />
        ) : null
      }
      vazio={
        <EstadoVazio
          titulo="Nenhum aparelho com biometria"
          descricao="Para entrar com o rosto ou a digital, abra o sistema no celular e ative em Perfil › Segurança."
        />
      }
      rodape={<RodapeDeContagem total={lista.length} />}
    />
  );
}

/* ------------------------------------------------------------------------- */

function Bloco({
  titulo,
  apoio,
  children,
}: {
  readonly titulo: string;
  readonly apoio?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="cartao flex flex-col gap-4 p-6">
      <div className="flex flex-col gap-2">
        <h2 className="txt-secao text-tinta">{titulo}</h2>
        {apoio === undefined ? null : (
          <p className="txt-apoio max-w-[72ch] text-pretty text-tinta-secundaria">{apoio}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function Secao({
  titulo,
  apoio,
  children,
}: {
  readonly titulo: string;
  readonly apoio?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-t border-borda-sutil pt-4 first:border-t-0 first:pt-0">
      <div className="flex flex-col gap-1">
        <h3 className="txt-subtitulo text-tinta">{titulo}</h3>
        {apoio === undefined ? null : (
          <p className="txt-apoio max-w-[72ch] text-pretty text-tinta-secundaria">{apoio}</p>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Um item com a DERIVACAO colada nele.
 *
 * `porque` nao e enfeite e nao e opcional: "Almoxarifado vê Combustível" e uma
 * afirmacao solta, e "necessidade: saldo, reserva e demanda" e uma afirmacao
 * que da para conferir contra a tabela 1.4 e discordar. A tela inteira existe
 * para essa segunda forma.
 */
function Item({
  titulo,
  marcador,
  porque,
  children,
}: {
  readonly titulo: string;
  readonly marcador: ReactNode;
  readonly porque: string;
  readonly children?: ReactNode;
}) {
  return (
    <li className="flex flex-col gap-1">
      <span className="flex flex-wrap items-center gap-2">
        <span className="txt-corpo text-tinta">{titulo}</span>
        {marcador}
      </span>
      {children ? <span className="txt-apoio text-tinta-secundaria">{children}</span> : null}
      <span className="txt-apoio text-tinta-apagada">{porque}</span>
    </li>
  );
}

function ItemSimples({
  titulo,
  children,
}: {
  readonly titulo: string;
  readonly children: ReactNode;
}) {
  return (
    <li className="flex flex-col gap-1">
      <span className="txt-corpo text-tinta">{titulo}</span>
      <span className="txt-apoio text-tinta-secundaria">{children}</span>
    </li>
  );
}

/**
 * Marcador de contorno neutro, pelo mesmo motivo do `BadgePrioridade`: as cores
 * de status sao exclusivas do status (p.09) e nenhum outro elemento da
 * interface pode usa-las. Quem carrega o significado e a palavra escrita.
 */
function Marcador({ children }: { readonly children: ReactNode }) {
  return (
    <span className="inline-flex h-[var(--selo-altura)] items-center rounded-badge border border-borda px-2">
      <span className="txt-badge text-tinta">{children}</span>
    </span>
  );
}

/**
 * A IDENTIDADE DO PERFIL, COM A ORIGEM NO TOPO.
 *
 * Os dois ramos nao sao o mesmo cartao com um titulo diferente, e nao podiam
 * ser: o perfil do guia mostra "Necessidade principal" e "Decisoes apoiadas",
 * que sao COLUNAS da secao 1.4 e existem para ser conferidas contra o
 * documento; o perfil decidido nao tem linha na 1.4 nenhuma, e mostra o pedido
 * com as palavras de quem pediu, quem decidiu e quando. O tipo garante isso
 * sozinho — `PerfilDecididoPeloProjeto` nao tem os dois campos, entao este
 * componente nao tem como escrever "Necessidade principal" para o
 * administrador nem por descuido.
 */
function CartaoDoPerfil({ perfil }: { readonly perfil: PerfilDisponivel }) {
  if (perfil.origem === 'guia') {
    return (
      <div className="bloco-rebaixado flex flex-col gap-3 p-4">
        <span className="txt-rotulo text-tinta-secundaria">Do guia de construção</span>
        <Linha rotulo="Perfil" valor={perfil.nome} />
        <Linha rotulo="Necessidade principal" valor={perfil.necessidade} />
        <Linha rotulo="Decisões apoiadas" valor={perfil.decisoes} />
      </div>
    );
  }

  return (
    <div className="bloco-rebaixado flex flex-col gap-3 p-4">
      <span className="flex flex-wrap items-center gap-2">
        <span className="txt-rotulo text-tinta-secundaria">
          Decisão do projeto, fora do guia de construção
        </span>
        <Marcador>Fora do guia</Marcador>
      </span>
      <Linha rotulo="Perfil" valor={perfil.nome} />
      <Linha rotulo="O que o dono pediu" valor={`“${perfil.pedido}”`} />
      <Linha rotulo="Quem decidiu" valor={`${perfil.decididoPor}, em ${perfil.decididoEm}`} />
      <Linha rotulo="Por que não está no guia" valor={perfil.foraDoGuia} />
      <ul className="flex flex-col gap-2 border-t border-borda-sutil pt-3">
        {perfil.ressalvas.map((ressalva) => (
          <li key={ressalva} className="txt-apoio max-w-[72ch] text-pretty text-tinta-secundaria">
            {ressalva}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Linha({ rotulo, valor }: { readonly rotulo: string; readonly valor: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</span>
      <span className="txt-corpo text-pretty text-tinta [overflow-wrap:anywhere]">{valor}</span>
    </div>
  );
}

/* Texto corrido em coluna de leitura (R01, p.27: coluna estreita so para texto
   corrido), e nao em linhas de 1.300 px. */
function Aviso({ children }: { readonly children: ReactNode }) {
  return <li className="txt-apoio max-w-[72ch] text-pretty text-tinta-secundaria">{children}</li>;
}
